import React from 'react';

const ArchitectureSection = () => {
  return (
    <section className="relative bg-white py-24 md:py-32 overflow-hidden">
      {/* CSS for subtle animations */}
      <style>{`
        /* Stake for Access - gentle breathing glow */
        @keyframes stake-glow {
          0%, 100% { 
            transform: scale(1); 
            box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          }
          50% { 
            transform: scale(1.015); 
            box-shadow: 0 8px 24px rgba(16, 185, 129, 0.12), 0 4px 12px rgba(0,0,0,0.06);
          }
        }
        .stake-card { 
          animation: stake-glow 4s ease-in-out infinite; 
        }
        
        /* Floating key with gentle rotation */
        @keyframes float-key {
          0%, 100% { 
            transform: rotate(12deg) translateY(0) scale(1); 
          }
          50% { 
            transform: rotate(15deg) translateY(-4px) scale(1.05); 
          }
        }
        .floating-key { 
          animation: float-key 3s ease-in-out infinite; 
        }
        
        /* Rewards - sequential zoom-in pulse effect */
        @keyframes reward-zoom-1 {
          0%, 15%, 100% { 
            transform: scale(1); 
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            border-color: rgb(229, 231, 235);
          }
          5%, 10% { 
            transform: scale(1.03); 
            box-shadow: 0 8px 20px rgba(16, 185, 129, 0.18);
            border-color: rgb(167, 243, 208);
          }
        }
        @keyframes reward-zoom-2 {
          0%, 20%, 45%, 100% { 
            transform: scale(1); 
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            border-color: rgb(229, 231, 235);
          }
          30%, 40% { 
            transform: scale(1.03); 
            box-shadow: 0 8px 20px rgba(16, 185, 129, 0.18);
            border-color: rgb(167, 243, 208);
          }
        }
        @keyframes reward-zoom-3 {
          0%, 50%, 75%, 100% { 
            transform: scale(1); 
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            border-color: rgb(229, 231, 235);
          }
          60%, 70% { 
            transform: scale(1.03); 
            box-shadow: 0 8px 20px rgba(16, 185, 129, 0.18);
            border-color: rgb(167, 243, 208);
          }
        }
        .reward-card-1 { 
          animation: reward-zoom-1 6s ease-in-out infinite;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .reward-card-2 { 
          animation: reward-zoom-2 6s ease-in-out infinite;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .reward-card-3 { 
          animation: reward-zoom-3 6s ease-in-out infinite;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        
        /* Hover enhancement for reward cards */
        .reward-card-1:hover,
        .reward-card-2:hover,
        .reward-card-3:hover {
          transform: scale(1.03) !important;
          box-shadow: 0 8px 20px rgba(16, 185, 129, 0.2) !important;
          border-color: rgb(167, 243, 208) !important;
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-6">
        {/* Section Title */}
        <div className="mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">
            <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">The Economic Layer of $QS.</span>{' '}
            <span className="text-gray-500 font-normal">Aligned incentives to everyone.</span>
          </h2>
        </div>

        {/* Single Container with asymmetric grid */}
        <div className="border border-gray-200 rounded-3xl overflow-hidden bg-gray-50">
          {/* Top Row - Left larger (60%), Right smaller (40%) */}
          <div className="grid md:grid-cols-[60%_40%] border-b border-gray-200">
            {/* Block 1 - Top Left (Larger) - Ecosystem Distribution */}
            <div className="p-8 md:p-10 min-h-[350px] flex flex-col md:border-r border-gray-200">
              {/* Header */}
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Ecosystem Distribution
                </h3>
                <p className="text-gray-500 text-sm">
                  50% of InteractionFi volume flows back to $QS holders ecosystem-wide.
                </p>
              </div>
              
              {/* Visual - Revenue Sharing Diagram */}
              <div className="flex-1 flex items-center justify-center">
                <div className="relative w-full max-w-md">
                  {/* Central Circle - Revenue Pool */}
                  <div className="relative mx-auto w-32 h-32 rounded-full bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100 flex items-center justify-center shadow-lg">
                    <div className="text-center">
                      <span className="block text-3xl font-bold text-emerald-600">50%</span>
                      <span className="text-xs text-gray-500">Revenue</span>
                    </div>
                  </div>
                  
                  {/* Holder Icons around the circle */}
                  <div className="absolute -top-4 left-4 w-10 h-10 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center shadow-md">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div className="absolute -top-4 right-4 w-10 h-10 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center shadow-md">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div className="absolute top-1/2 -left-8 -translate-y-1/2 w-10 h-10 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center shadow-md">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div className="absolute top-1/2 -right-8 -translate-y-1/2 w-10 h-10 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center shadow-md">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div className="absolute -bottom-4 left-4 w-10 h-10 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center shadow-md">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div className="absolute -bottom-4 right-4 w-10 h-10 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center shadow-md">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  
                  {/* Small floating coins/tokens */}
                  <div className="absolute top-2 left-16 w-6 h-6 rounded-full bg-emerald-400 flex items-center justify-center text-white text-xs font-bold shadow animate-bounce" style={{animationDuration: '2s'}}>$</div>
                  <div className="absolute top-8 right-12 w-6 h-6 rounded-full bg-teal-400 flex items-center justify-center text-white text-xs font-bold shadow animate-bounce" style={{animationDuration: '2.5s'}}>$</div>
                  <div className="absolute bottom-8 left-12 w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs font-bold shadow animate-bounce" style={{animationDuration: '3s'}}>$</div>
                </div>
              </div>
            </div>

            {/* Block 2 - Top Right (Smaller) - Lower Fees */}
            <div className="p-8 md:p-10 min-h-[350px] flex flex-col">
              {/* Header */}
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Lower Fees
                </h3>
                <p className="text-gray-500 text-sm">
                  Use $QS for platform payments and get 15% off fees.
                </p>
              </div>
              
              {/* Visual - Fee Discount Display */}
              <div className="flex-1 flex items-center justify-center">
                <div className="relative">
                  {/* Main Discount Card */}
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6 w-56">
                    {/* Strikethrough original */}
                    <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
                      <span className="text-gray-400 text-sm">Standard Fee</span>
                      <span className="text-gray-400 line-through text-lg">$100.00</span>
                    </div>
                    
                    {/* Discounted price */}
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-gray-700 text-sm font-medium">With $QS</span>
                      <span className="text-emerald-600 font-bold text-2xl">$85.00</span>
                    </div>
                    
                    {/* Savings badge */}
                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-3 border border-emerald-100">
                      <div className="flex items-center justify-center gap-2 whitespace-nowrap">
                        <svg className="w-5 h-5 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-emerald-700 font-semibold text-sm">You Save $15.00</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Floating 15% OFF Badge */}
                  <div className="absolute -top-3 -right-3 w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center shadow-lg transform rotate-12">
                    <div className="text-center text-white">
                      <span className="block text-lg font-bold leading-none">15%</span>
                      <span className="block text-xs font-medium">OFF</span>
                    </div>
                  </div>
                  
                  {/* Small $QS token icon */}
                  <div className="absolute -bottom-2 -left-2 w-10 h-10 bg-white rounded-full border-2 border-emerald-200 flex items-center justify-center shadow-md">
                    <span className="text-emerald-600 font-bold text-sm">$QS</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Row - Left smaller (40%), Right larger (60%) */}
          <div className="grid md:grid-cols-[40%_60%]">
            {/* Block 3 - Bottom Left (Smaller) - Stake for Access */}
            <div className="p-8 md:p-10 min-h-[350px] flex flex-col md:border-r border-gray-200">
              {/* Header */}
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Stake for Access
                </h3>
                <p className="text-gray-500 text-sm">
                  Stake $QS to unlock premium features and advanced capabilities.
                </p>
              </div>
              
              {/* Visual - Staking Unlock */}
              <div className="flex-1 flex items-center justify-center">
                <div className="relative">
                  {/* Main Card */}
                  <div className="stake-card bg-white rounded-2xl border border-gray-200 shadow-lg p-5 w-52">
                    {/* Unlocked Badge */}
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100 flex items-center justify-center">
                        <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <span className="text-sm font-semibold text-gray-900">Premium Unlocked</span>
                    </div>
                    
                    {/* Feature List */}
                    <div className="space-y-2.5">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-xs text-gray-600">Priority Access</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-xs text-gray-600">Advanced Tools</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-xs text-gray-600">Exclusive Features</span>
                      </div>
                    </div>
                    
                    {/* Staking Amount */}
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-2.5 border border-emerald-100">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">Staked</span>
                          <span className="text-sm font-bold text-emerald-600">100,000 $QS</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Floating Key Icon */}
                  <div className="floating-key absolute -top-3 -right-3 w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center shadow-lg">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Block 4 - Bottom Right (Larger) - Rewards */}
            <div className="p-8 md:p-10 min-h-[350px] flex flex-col">
              {/* Header */}
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Rewards
                </h3>
                <p className="text-gray-500 text-sm">
                  Earn through in-session activity, referrals, and meaningful participation.
                </p>
              </div>
              
              {/* Visual - Reward Streams */}
              <div className="flex-1 flex items-center justify-center">
                <div className="w-full max-w-sm space-y-3">
                  {/* Reward Card 1 - Activity */}
                  <div className="reward-card-1 bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4 transition-all">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">In-Session Activity</p>
                      <p className="text-xs text-gray-500">Engage & earn rewards</p>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-bold text-emerald-600">+50 points</span>
                    </div>
                  </div>
                  
                  {/* Reward Card 2 - Referrals */}
                  <div className="reward-card-2 bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4 transition-all">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">Referrals</p>
                      <p className="text-xs text-gray-500">Invite friends & grow</p>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-bold text-emerald-600">+100 points</span>
                    </div>
                  </div>
                  
                  {/* Reward Card 3 - Participation */}
                  <div className="reward-card-3 bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4 transition-all">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">Participation</p>
                      <p className="text-xs text-gray-500">Meaningful contributions</p>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-bold text-emerald-600">+75 points</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quicks Footer Strip - Connector Layer */}
          <div className="border-t border-gray-200 bg-gradient-to-r from-gray-50 via-white to-gray-50">
            <div className="px-8 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
              {/* Left side - Quicks branding */}
              <div className="flex items-center gap-4">
                <svg className="w-8 h-8 text-emerald-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
                <div>
                  <h4 className="text-lg font-bold text-gray-900">
                    Quicks: <span className="text-gray-500 font-medium">Stable Usage Credits</span>
                  </h4>
                  <p className="text-sm text-gray-500">
                    Used for payments, subscriptions, and interactions across the QS ecosystem.
                  </p>
                </div>
              </div>
              
              {/* Right side - Visual connector showing $QS → Quicks relationship */}
              <div className="flex items-center gap-3 text-sm">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full border border-gray-200 shadow-sm">
                  <span className="font-semibold text-emerald-600">$QS</span>
                  <span className="text-gray-400 text-xs">ownership</span>
                </div>
                <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-full border border-emerald-100 shadow-sm">
                  <span className="font-semibold text-emerald-600">Quicks</span>
                  <span className="text-gray-400 text-xs">usage</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ArchitectureSection;
